/**
 * A category-axis label that recharts would otherwise render as a single unbroken
 * SVG `<text>` — clipped by the chart's own viewBox once it runs past the axis
 * width, with no wrapping or truncation of its own. Two Promotions charts hit
 * this: promotion names (operator-authored, unbounded length) and skip-reason
 * sentences (translated to Persian at render time, often longer than their
 * English source), both flip their axis to the RIGHT edge in RTL — exactly
 * where the illegible, overlapping text was reported.
 *
 * `getBBox`/canvas text measurement isn't available in every environment this
 * renders in (including jsdom in tests), so this truncates by character count
 * against a conservative per-character width estimate rather than measuring
 * the actual glyphs. It undercounts a little for narrow characters and
 * overcounts for wide ones — the goal is "never overflows the axis", not a
 * pixel-perfect fit.
 */

const AVERAGE_CHAR_WIDTH_PX = 7

export function truncateForAxis(label: string, maxWidthPx: number): string {
  const maxChars = Math.max(1, Math.floor(maxWidthPx / AVERAGE_CHAR_WIDTH_PX))
  // Array.from splits on code points, not UTF-16 code units — a plain .length/.slice()
  // would cut an astral-plane character (e.g. an emoji in a promotion name) in half at
  // the truncation boundary and render a broken glyph.
  const chars = Array.from(label)
  if (chars.length <= maxChars) {
    return label
  }
  if (maxChars <= 1) {
    return '…'
  }
  return `${chars.slice(0, maxChars - 1).join('')}…`
}

interface TickProps {
  x?: number
  y?: number
  textAnchor?: string
  fontSize?: number
  className?: string
  fill?: string
  stroke?: string
  payload?: { value: string }
}

/**
 * `formatter` is applied here rather than left to recharts' own `tickFormatter` prop:
 * recharts does not document what a custom `tick` component receives when
 * `tickFormatter` is also set, and getting that wrong would silently show an
 * untranslated key instead of the Persian sentence. Passing the formatter straight
 * into the tick removes the ambiguity — the caller drops `tickFormatter` entirely
 * once it supplies a custom `tick`.
 *
 * Only forwards the props recharts itself renders on a default tick's `<text>`
 * (`className`, `fill`, `stroke`, ...) rather than spreading every prop recharts
 * passes in — several of those (`verticalAnchor`, `orientation`, `visibleTicksCount`,
 * ...) are layout metadata for recharts' own renderer, not valid SVG attributes, and
 * `className` in particular carries `recharts-cartesian-axis-tick-value`, which
 * both recharts' own styling and this screen's own tests select on.
 */
export function makeTruncatedCategoryTick(
  maxWidthPx: number,
  formatter: (value: string) => string = (value) => value
) {
  return function TruncatedCategoryTick(props: TickProps) {
    const {
      x = 0,
      y = 0,
      textAnchor,
      fontSize = 12,
      className,
      fill,
      stroke,
      payload
    } = props
    const rawValue = payload?.value ?? ''
    // `getTranslate`'s dictionary lookup (`dict[text] || text`) has no `hasOwnProperty`
    // guard: a promotion literally named `constructor`, `toString`, `hasOwnProperty`, etc.
    // resolves to that Object.prototype member instead of the fallback string, handing a
    // function (or, for `__proto__`, throwing before it even gets here) to code that
    // assumes a string. Falling back to the untranslated raw value keeps the chart
    // rendering instead of crashing on an admin-authored edge-case name.
    const formatted = formatter(rawValue)
    const fullLabel = typeof formatted === 'string' ? formatted : rawValue
    const truncated = truncateForAxis(fullLabel, maxWidthPx)

    return (
      <text
        x={x}
        y={y}
        dy={4}
        textAnchor={textAnchor}
        fontSize={fontSize}
        className={className}
        fill={fill}
        stroke={stroke}>
        {truncated !== fullLabel && <title>{fullLabel}</title>}
        {truncated}
      </text>
    )
  }
}
